import { api } from './client';
import type { Page } from './catalogs';

export interface TaxInvoiceOutgoingItem {
  id: string;
  line_number: number;
  nomenclature_id: string | null;
  description: string;
  ukt_zed_code: string | null;
  uom_code: string | null;
  quantity: string;
  price_without_vat: string;
  vat_rate_id: string;
  base_amount: string;
  vat_amount: string;
  /** ч.900: GOODS | SERVICE | FA | IA — вкладки Товари/Послуги/ОЗ/НМА як у 1С. */
  row_kind?: string;
}

export interface TaxInvoiceOutgoingListItem {
  id: string;
  organization_id: string;
  is_correction?: boolean;
  invoice_number: string;
  invoice_date: string;
  contractor_id: string;
  contractor_name: string;
  total_amount: string;
  erpn_status: string;
  erpn_response?: string | null;
  erpn_registration_date?: string | null;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
  base_document_id?: string | null;
}

export interface TaxInvoiceOutgoing extends TaxInvoiceOutgoingListItem {
  comment?: string | null;
  is_correction: boolean;
  corrected_invoice_id?: string | null;
  correction_reason?: string | null;
  contract_id: string | null;
  contractor_edrpou: string | null;
  contractor_inn: string | null;
  invoice_type: string;
  base_20: string;
  vat_20: string;
  base_7: string;
  vat_7: string;
  base_14: string;
  vat_14: string;
  base_0: string;
  base_exempt: string;
  erpn_registration_date: string | null;
  erpn_response?: string | null;
  base_document_type: string | null;
  base_document_id: string | null;
  posted_at: string | null;
  items: TaxInvoiceOutgoingItem[];
}

export interface TaxInvoiceOutgoingCreate {
  organization_id: string;
  base_document_type: 'doc.goods_sales' | 'doc.production_service_acts' | 'doc.processing_services_sales';
  base_document_id: string;
  invoice_number?: string;
  invoice_date?: string;
}

/** Рядок ПН у ручному редакторі — base/vat рахує сервер.
 *  ч.891: quantity=0 → підсумковий рядок (1С УТП), тоді base_amount/vat_amount явні. */
export interface TaxInvoiceItemIn {
  nomenclature_id?: string | null;
  description: string;
  ukt_zed_code?: string | null;
  uom_code?: string | null;
  quantity: string;
  price_without_vat: string;
  vat_rate_id: string;
  base_amount?: string | null;
  vat_amount?: string | null;
  row_kind?: 'GOODS' | 'SERVICE' | 'FA' | 'IA';
}

export interface TaxInvoiceOutgoingManualCreate {
  comment?: string | null;
  organization_id: string;
  invoice_number?: string | null;
  invoice_date: string;
  contractor_id: string;
  contract_id?: string | null;
  not_issued?: boolean;
  not_issued_reason?: string | null;
  base_document_type?: 'doc.goods_sales' | 'doc.production_service_acts' | 'doc.processing_services_sales'
    | 'doc.bank_receipts' | 'doc.cash_receipts' | null;
  base_document_id?: string | null;
  items: TaxInvoiceItemIn[];
}

export interface TaxInvoiceOutgoingUpdatePayload {
  comment?: string | null;
  invoice_number?: string;
  invoice_date?: string;
  contractor_id?: string;
  contract_id?: string | null;
  not_issued?: boolean;
  not_issued_reason?: string | null;
  base_document_type?: 'doc.goods_sales' | 'doc.production_service_acts' | 'doc.processing_services_sales'
    | 'doc.bank_receipts' | 'doc.cash_receipts' | null;
  base_document_id?: string | null;
  items?: TaxInvoiceItemIn[];
}

/** ч.1447: ПН на аванс прямо з надходження — що показати в надходженні. */
export type ReceiptType = 'doc.bank_receipts' | 'doc.cash_receipts';
export interface AdvanceFromReceiptState {
  /** invoice — ПН є; registration — є реєстрація авансу; create — створити ПН;
   *  create_registration — надходження проведено без ПДВ з авансу, ПН через реєстрацію; none — аванс не виникає. */
  mode: 'invoice' | 'registration' | 'create' | 'create_registration' | 'none';
  reason: string | null;
  advance_amount: number;
  invoice_id: string | null;
  invoice_number: string | null;
  invoice_date: string | null;
  invoice_posted: boolean | null;
  registration_id: string | null;
}

export const taxInvoicesOutgoing = {
  list: (params?: {
    organization_id?: string;
    contractor_id?: string;
    posted?: boolean;
    date_from?: string;
    date_to?: string;
    limit?: number; offset?: number; sort_by?: string; sort_dir?: 'asc' | 'desc'; is_correction?: boolean;
  }) =>
    api.get<Page<TaxInvoiceOutgoingListItem>>('/tax-invoices-outgoing', { params })
      .then((r) => r.data),
  get: (id: string) =>
    api.get<TaxInvoiceOutgoing>(`/tax-invoices-outgoing/${id}`).then((r) => r.data),
  createFromSale: (payload: TaxInvoiceOutgoingCreate) =>
    api.post<TaxInvoiceOutgoing>('/tax-invoices-outgoing/from-sale', payload)
      .then((r) => r.data),
  advanceFromReceiptState: (organizationId: string, receiptType: ReceiptType, receiptId: string) =>
    api.get<AdvanceFromReceiptState>('/tax-invoices-outgoing/advance-from-receipt', {
      params: { organization_id: organizationId, receipt_type: receiptType, receipt_id: receiptId },
    }).then((r) => r.data),
  createAdvanceFromReceipt: (organizationId: string, receiptType: ReceiptType, receiptId: string) =>
    api.post<TaxInvoiceOutgoing>('/tax-invoices-outgoing/advance-from-receipt', {
      organization_id: organizationId, receipt_type: receiptType, receipt_id: receiptId,
    }).then((r) => r.data),
  createManual: (payload: TaxInvoiceOutgoingManualCreate) =>
    api.post<TaxInvoiceOutgoing>('/tax-invoices-outgoing', payload)
      .then((r) => r.data),
  // ч.1352: підстава для вже створеної (і проведеної) ПН — реалізація, створена з неї.
  linkBase: (id: string, payload: { base_document_type: string; base_document_id: string }) =>
    api.post<TaxInvoiceOutgoing>(`/tax-invoices-outgoing/${id}/link-base`, payload).then((r) => r.data),
  update: (id: string, payload: TaxInvoiceOutgoingUpdatePayload) =>
    api.patch<TaxInvoiceOutgoing>(`/tax-invoices-outgoing/${id}`, payload)
      .then((r) => r.data),
  createCorrectionFromReturn: (payload: TaxInvoiceOutgoingCorrectionCreate) =>
    api.post<TaxInvoiceOutgoing>('/tax-invoices-outgoing/correction-from-return', payload)
      .then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/tax-invoices-outgoing/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<TaxInvoiceOutgoing>(`/tax-invoices-outgoing/${id}/post`)
      .then((r) => r.data),
  unpost: (id: string) =>
    api.post<TaxInvoiceOutgoing>(`/tax-invoices-outgoing/${id}/unpost`)
      .then((r) => r.data),
  // ЄРПН: XML J1201016 для підпису КЕП (повна axios-відповідь — ім'я файлу
  // ДПС береться з Content-Disposition, як у dpsRequests.xml).
  erpnXml: (id: string) =>
    api.get(`/tax-invoices-outgoing/${id}/erpn-xml`, { responseType: 'blob' }),
  // Чернетка подачі у Кабінеті ДПС (ідемпотентно) → submission_id.
  // ч.898: ручна позначка реєстрації в ЄРПН (ПН, зареєстровані поза сервісом).
  erpnMarkRegistered: (id: string, registration_date: string) =>
    api.post<TaxInvoiceOutgoing>(`/tax-invoices-outgoing/${id}/erpn-mark-registered`,
      { registration_date }).then((r) => r.data),
  erpnUnmarkRegistered: (id: string) =>
    api.post<TaxInvoiceOutgoing>(`/tax-invoices-outgoing/${id}/erpn-unmark-registered`)
      .then((r) => r.data),
  erpnDraft: (id: string) =>
    api.post<{
      submission_id: string; file_name: string; registered_by_buyer?: boolean;
    }>(`/tax-invoices-outgoing/${id}/erpn-draft`).then((r) => r.data),
};

// ==================== Incoming ====================

export interface TaxInvoiceIncomingItem {
  id: string;
  line_number: number;
  nomenclature_id: string | null;
  description: string;
  ukt_zed_code: string | null;
  uom_code: string | null;
  quantity: string;
  price_without_vat: string;
  vat_rate_id: string;
  base_amount: string;
  vat_amount: string;
}

export interface TaxInvoiceIncomingListItem {
  id: string;
  organization_id: string;
  invoice_number: string;
  invoice_date: string;
  contractor_id: string;
  contractor_name: string;
  total_amount: string;
  is_registered_erpn: boolean;
  // 2026-08-06: РК vs ПН у списку + «на підпис» (отримані з кабінету).
  is_correction?: boolean;
  sign_status?: string | null;
  // 0406: для РК — яку ПН коригує.
  corrected_number?: string | null;
  corrected_date?: string | null;
  registration_date?: string | null;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
  base_document_type?: string | null;
  base_document_id?: string | null;
}

/** Рядок звіту «Звірка ПДВ-кредиту» (2026-08-04). */
export interface VatCreditReconRow {
  kind: 'receipt' | 'advance';
  /** Статус рядка (сервер): без ПН / ПН не зареєстрована / ок. */
  status?: 'missing' | 'unregistered' | 'ok';
  doc_id: string;
  doc_number: string;
  doc_date: string;
  contractor_id: string;
  contractor_name: string;
  amount: string;
  expected_vat: string;
  expected_estimated: boolean;
  pn_id: string | null;
  pn_number: string | null;
  pn_registered: boolean | null;
  pn_source: string | null;
  pn_registration_date: string | null;
  pn_posted: boolean | null;
  pn_vat: string | null;
}

export interface VatCreditReconTotals {
  receipts_count: number;
  receipts_expected_vat: string;
  receipts_missing_count: number;
  receipts_missing_vat: string;
  advances_count: number;
  advances_missing_count: number;
  advances_missing_vat: string;
  pn_vat_total: string;
  pn_unregistered_count: number;
}

export interface TaxInvoiceIncoming extends TaxInvoiceIncomingListItem {
  comment?: string | null;
  contract_id: string | null;
  contractor_edrpou: string | null;
  contractor_inn: string | null;
  is_correction: boolean;
  invoice_type: string;
  base_20: string;
  vat_20: string;
  base_7: string;
  vat_7: string;
  base_14: string;
  vat_14: string;
  base_0: string;
  base_exempt: string;
  registration_date: string | null;
  base_document_type: string | null;
  base_document_id: string | null;
  posted_at: string | null;
  items: TaxInvoiceIncomingItem[];
}

export interface TaxInvoiceIncomingManualCreate {
  comment?: string | null;
  organization_id: string;
  invoice_number: string;
  invoice_date: string;
  contractor_id: string;
  contract_id?: string | null;
  is_registered_erpn?: boolean;
  registration_date?: string | null;
  base_document_type?: string | null;  // ч.906: прихід / платіжка / РКО / авансовий звіт / ВМД / замовлення (з 1С)
  base_document_id?: string | null;
  items: TaxInvoiceItemIn[];
}

export interface TaxInvoiceIncomingUpdatePayload {
  comment?: string | null;
  invoice_number?: string;
  invoice_date?: string;
  contractor_id?: string;
  contract_id?: string | null;
  is_registered_erpn?: boolean;
  registration_date?: string | null;
  base_document_type?: string | null;  // ч.906: як у ManualCreate
  base_document_id?: string | null;
  items?: TaxInvoiceItemIn[];
}

export interface TaxInvoiceIncomingCreate {
  organization_id: string;
  base_document_type: 'doc.goods_receipts';
  base_document_id: string;
  invoice_number: string;
  invoice_date: string;
  is_registered_erpn?: boolean;
  registration_date?: string;
}

export interface TaxInvoiceOutgoingCorrectionCreate {
  organization_id: string;
  return_id: string;
  corrected_invoice_id: string;
  invoice_number: string;
  invoice_date: string;
  correction_reason?: string | null;
}

export interface TaxInvoiceIncomingCorrectionCreate {
  organization_id: string;
  return_id: string;
  corrected_invoice_id: string;
  invoice_number: string;
  invoice_date: string;
  is_registered_erpn?: boolean;
  registration_date?: string;
}

export const taxInvoicesIncoming = {
  list: (params?: {
    organization_id?: string;
    contractor_id?: string;
    posted?: boolean;
    date_from?: string;
    date_to?: string;
    limit?: number; offset?: number; sort_by?: string; sort_dir?: 'asc' | 'desc'; is_correction?: boolean;
  }) =>
    api.get<Page<TaxInvoiceIncomingListItem>>('/tax-invoices-incoming', { params })
      .then((r) => r.data),
  get: (id: string) =>
    api.get<TaxInvoiceIncoming>(`/tax-invoices-incoming/${id}`).then((r) => r.data),
  // Звірка ПДВ-кредиту за період (2026-08-04): приходи + аванси ± ПН.
  // 2026-08-22: серверна пагінація — конверт items/total + totals за період.
  creditReconciliation: (p: {
    organization_id: string; date_from: string; date_to: string;
    status?: 'missing' | 'unregistered' | 'ok';
    sort_by?: string; sort_dir?: 'asc' | 'desc';
    limit?: number; offset?: number;
  }) =>
    api.get<{ items: VatCreditReconRow[]; total: number; limit: number;
      offset: number; totals: VatCreditReconTotals }>(
      '/tax-invoices-incoming/credit-reconciliation', { params: p }).then((r) => r.data),
  createFromReceipt: (payload: TaxInvoiceIncomingCreate) =>
    api.post<TaxInvoiceIncoming>('/tax-invoices-incoming/from-receipt', payload)
      .then((r) => r.data),
  createManual: (payload: TaxInvoiceIncomingManualCreate) =>
    api.post<TaxInvoiceIncoming>('/tax-invoices-incoming', payload)
      .then((r) => r.data),
  // 2026-07-31: імпорт вхідних ПН з виписки ЄРПН (ZIP/XML з кабінету).
  erpnImport: (organizationId: string, file: globalThis.File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<{
      created: number; matched: number; skipped_existing: number;
      not_ours: string[]; no_contractor: string[];
      unmatched: string[]; errors: string[];
    }>(`/tax-invoices-incoming/erpn-import?organization_id=${organizationId}`,
      fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data);
  },
  // ч.1352: надходження, створене з вхідної ПН, стає її підставою.
  linkBase: (id: string, payload: { base_document_type: string; base_document_id: string }) =>
    api.post<TaxInvoiceIncoming>(`/tax-invoices-incoming/${id}/link-base`, payload).then((r) => r.data),
  update: (id: string, payload: TaxInvoiceIncomingUpdatePayload) =>
    api.patch<TaxInvoiceIncoming>(`/tax-invoices-incoming/${id}`, payload)
      .then((r) => r.data),
  createCorrectionFromReturn: (payload: TaxInvoiceIncomingCorrectionCreate) =>
    api.post<TaxInvoiceIncoming>('/tax-invoices-incoming/correction-from-return', payload)
      .then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/tax-invoices-incoming/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<TaxInvoiceIncoming>(`/tax-invoices-incoming/${id}/post`)
      .then((r) => r.data),
  unpost: (id: string) =>
    api.post<TaxInvoiceIncoming>(`/tax-invoices-incoming/${id}/unpost`)
      .then((r) => r.data),
};
